import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const txRef = searchParams.get('tx_ref');
      const status = searchParams.get('status');

      if (!txRef) {
        setError('Transaction invalide');
        setVerifying(false);
        return;
      }

      if (status === 'cancelled') {
        setError('Paiement annulé');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/verify/${txRef}`);
        
        if (response.data.success && response.data.status === 'successful') {
          setSuccess(true);
          toast.success('Paiement confirmé! Votre compte Premium est maintenant actif.');
          
          // Recharger les données utilisateur
          const token = localStorage.getItem('token');
          const userResponse = await axios.get(`${API}/users/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem('user', JSON.stringify(userResponse.data));
          
          // Rediriger après 3 secondes
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setError('Paiement non confirmé. Veuillez contacter le support.');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError('Erreur lors de la vérification du paiement');
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-16 w-16 animate-spin text-sky-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Vérification du paiement...</h2>
            <p className="text-slate-600">Veuillez patienter</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-red-900">Paiement échoué</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex-1"
              >
                Retour au dashboard
              </Button>
              <Button
                onClick={() => navigate('/checkout', { state: { amount: 1.0, paymentType: 'subscription' } })}
                className="flex-1 bg-sky-500 hover:bg-sky-600"
              >
                Réessayer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-green-900">Paiement réussi !</h2>
          <p className="text-slate-600 mb-6">
            Votre compte Premium est maintenant actif. Vous allez être redirigé vers votre dashboard...
          </p>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-800">
              <strong>Félicitations!</strong> Vous avez maintenant accès à toutes les fonctionnalités Premium.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentCallback;
